/**
 * **내 화면** — 사이드바에 서는 줄을 사용자가 정한다. 워크스페이스 문서의 `menuSets` 한 필드다.
 *
 * ```
 *   CatchMomentsWorkspace/{id}.menuSets   줄 목록(이름·보임·차례·무엇을 여나)
 *     │ workspace.byId 로 이미 실려 온다
 *   이 파일            읽기·기본값·고치기 (순수)
 *     ▼
 *   사이드바 「내 화면」 · 추적 관리의 다섯째 구역
 * ```
 *
 * 🔑 **줄은 «무엇을 여는가»로 갈린다** — 셋이다.
 *   `builtin` 기존 화면 그대로(브랜드 멘션·타겟 키워드 수집·자사 계정 관리) ·
 *   `campaign` 등록된 수집 캠페인의 조건으로 분석 화면 · `own` 여기서 적은 조건으로 분석 화면.
 * 🚨 **`own` 은 수집을 걸지 않는다** — 이미 모인 것을 내 조건으로 보는 화면이다. 수집 세트
 *   (`CatchMomentsCampaigns`)를 만들면 플랜의 수집 슬롯을 먹으므로, 화면 하나 뽑는 값으로는
 *   너무 비싸다. 정말 모으고 싶으면 캠페인을 만들어 `campaign` 으로 잇는다.
 * ⚠️ **저장이 «없으면»(필드가 아예 없으면) 기본 셋을 준다** — 아무것도 안 고친 워크스페이스는
 *   지금과 똑같이 보인다. 처음 고치는 순간 목록이 통째로 저장된다(기본값을 문서에 미리 심지
 *   않는다 — 심으면 나중에 기본을 바꿀 수 없다).
 * 🚨 **저장된 목록은 그대로 따른다 — 빠진 기본 셋을 도로 채워 넣지 않는다.** 채워 넣으면
 *   사용자가 지운 기본 줄이 다음에 열 때 되살아난다(「지워지지 않는다」로 읽힌다). 빈 배열도
 *   «비우기로 한 것»이라 그대로 둔다. 대신 **되돌리는 길**을 연다(`restoreBuiltinSets`) —
 *   기본 셋은 만들기 폼으로 다시 만들 수 없어, 되돌릴 길이 없으면 한 번 지우면 끝이다.
 * 🔑 **줄이 여는 것은 언제나 브랜드 멘션 화면이다** — 대상과 필터만 갈아 끼운다. 그래서
 *   「미국으로 한 줄, 한국으로 한 줄」처럼 같은 대상을 조건만 달리해 여럿 세울 수 있다.
 * ⚠️ **플랜 관문은 줄이 아니라 «여는 화면»의 것이다** — 줄 이름을 바꿨다고 관문이 헐거워지지 않는다.
 */

import type { DashboardWidgetId, DashboardCustomWidget } from 'src/constants/dashboard-widgets';

import { ANALYTICS_CHARTS } from 'src/constants/pivot-charts';
import { MY_SCREENS_VISIBLE } from 'src/constants/my-screens-visibility';
import { ANALYTICS_AXES, ANALYTICS_METRICS } from 'src/constants/pivot-fields';
import {
  isDashboardWidgetId,
  DASHBOARD_COUNT_AXES,
  DASHBOARD_COUNT_DAYS,
  DASHBOARD_WIDGET_IDS,
  DASHBOARD_PIVOT_DAYS,
  toDashboardWidgetSpan,
  DASHBOARD_COUNT_DESIGNS,
  DASHBOARD_OVERVIEW_CARDS,
  isCustomDashboardWidgetId,
  DASHBOARD_OVERVIEW_DESIGNS,
} from 'src/constants/dashboard-widgets';

/**
 * **만들 수 있는 줄 수** — 사이드바가 목록이 되면 메뉴가 아니다.
 *
 * 🔑 **만들기를 막는 문**이다 — 이 수를 넘으면 만들기 폼이 아예 안 선다(`status-my-screens-card`).
 * 🚨 **이미 있는 줄을 잘라 내는 값이 아니다** — 읽어 들이는 상한은 `MENU_SET_CEILING` 이 따로
 *   쥔다. 한 값으로 쓰면 이 수를 내리는 순간 이미 서 있던 줄이 **말없이 사라진다**
 *   (수집 세트 줄은 세트마다 하나씩 서므로 세트가 많은 워크스페이스가 먼저 다친다).
 */
export const MENU_SET_MAX = 10;

/**
 * 읽어 들이는 **상한** — 저장본이 망가졌거나 세트가 아주 많을 때의 안전선.
 * ⚠️ 만들기 문(`MENU_SET_MAX`)보다 **넉넉해야** 한다 — 이미 서 있는 줄을 자르지 않는다.
 */
export const MENU_SET_CEILING = 50;

/** 기본으로 주는 화면들 — 지금 고정 메뉴로 서 있는 셋 */
export type BuiltinMenuKey = 'brandMention' | 'keywordMonitoring' | 'brandAccount';

/** 줄이 여는 것 */
export type MenuSetSource =
  | { kind: 'builtin'; key: BuiltinMenuKey }
  | { kind: 'campaign'; campaignId: string }
  | { kind: 'own' };

/**
 * 미리 지정해 두는 필터 — **화면의 사이드바가 쥐는 축 그대로**.
 *
 * 🔑 **사용자가 화면에서 실제로 만진 것을 그대로 담는다** — 만들기는 화면으로 넘어가 사이드바를
 *   보며 좁힌 뒤 저장하는 흐름이라, 담는 축이 사이드바보다 좁으면 만진 것이 소리 없이 버려진다.
 * 🚨 **키는 화이트리스트다**(`FILTER_KEYS`) — 사이드바가 쥔 객체를 통째로 담으면 워크스페이스
 *   문서에 모르는 값이 쌓인다. 모르는 키는 읽을 때 떨군다.
 * 🚨 **기간은 여기 없다** — 화면의 기간 고르개가 쥔다. 줄에 못 박아 두면 화면에서 기간을 바꿔도
 *   어느 쪽이 이겼는지 읽을 수 없다(분석 화면 사이드바도 같은 까닭으로 기간이 없다).
 */
export type MenuSetFilter = {
  /** 채널 — 빈 목록이 「전체」다 */
  platforms: string[];
  /** 심화 채널 — **브랜드 계열 화면에만 있는 칩**이다(X 인용·리포스트 · 인스타 스토리·포스트 · 틱톡 광고) */
  xPlatforms: string[];
  instagramPlatforms: string[];
  tiktokAdTypes: string[];
  tiktokAdBoosts: string[];
  /** 국가 코드(`KR`·`US`…) */
  country: string[];
  languages: string[];
  /**
   * 페르소나 — 광고/협찬 판정과 브랜드 계정 여부.
   * 🚨 **`boolean[]` 이다**(`string[]` 이 아니다) — 사이드바가 `adsEval.includes(true)` 로 읽는다.
   */
  adsEval: boolean[];
  isOfficialAccount: boolean[];
  /** 최소 반응 수치 — 0·없음이면 안 건다 */
  metricMin: number | null;
  excludeKeywords: string[];
  mandatoryKeywords: string[];
  /** 결과 안에서 다시 찾기 */
  refineText: string;
  /** 결과 내 재검색이 뒤지는 칸 — 비면 화면 기본값(본문·계정) */
  refineFieldKeys: string[];
  /** 정렬 열쇠 — 비면 화면 기본값 */
  sortKey: string;
  /**
   * 브랜드 계열 화면의 스위치 셋 — **꺼짐이 기본이 아닌 것이 있다.**
   * 🚨 그래서 `false` 도 «정한 값»으로 담는다 — 안 담으면 켜 둔 화면을 다시 열 때 꺼진다.
   * - `beautyEval` AI 뷰티 필터(뷰티로 판정된 글만)
   * - `canonicalBrandOnly` 브랜드 인식 기준(정식 이름으로 묶인 것만)
   * - `linkSearch` 링크 내 단어 포함(본문 링크 속 낱말까지 매칭)
   */
  beautyEval: boolean;
  canonicalBrandOnly: boolean;
  linkSearch: boolean;
};

/** 담는 축의 정본 — 여기 없는 키는 읽을 때 떨군다 */
const LIST_KEYS = [
  'platforms', 'xPlatforms', 'instagramPlatforms', 'tiktokAdTypes', 'tiktokAdBoosts',
  'country', 'languages', 'excludeKeywords', 'mandatoryKeywords', 'refineFieldKeys',
] as const;

/** 참·거짓을 담는 축 — 위와 달리 글이 아니라 `boolean` 이다 */
const FLAG_LIST_KEYS = ['adsEval', 'isOfficialAccount'] as const;

export type MenuSetAccount = { platform: string; account: string };

/** 사이드바 한 줄 */
export type MenuSet = {
  /** 줄을 가리키는 열쇠 — 기본 셋은 `builtin:<key>` 로 고정이다(이름을 바꿔도 안 변한다) */
  id: string;
  /** 사용자가 정한 이름 */
  name: string;
  /** 끈 줄 — 지우지 않고 감춘다(기본 셋은 지울 수 없고 감출 수만 있다) */
  hidden: boolean;
  source: MenuSetSource;
  /** `own` 일 때 보는 조회어 */
  keywords: string[];
  /** `own` 일 때 보는 계정 — 조회어와 **OR** 다(분석 화면과 같은 규약) */
  accounts: MenuSetAccount[];
  filter: MenuSetFilter;
  /**
   * **이 줄의 대시보드 차례** — 어느 칸을 어떤 순서로 그리나. 줄마다 따로 산다.
   * 🚨 **`null` 이 「브랜드 멘션 그대로」다** — 아무것도 안 고친 줄은 기본 화면과 한 칸도
   *   다르지 않게 보인다. 처음 빼거나 옮기는 순간에만 값이 생긴다.
   * ⚠️ 칸은 **무엇을 그리나만** 쥔다 — 조건·기간·필터는 화면 것을 따른다(칸마다 다른 조건을
   *   쥐면 한 판에 놓고 견줄 수 없다).
   */
  dashboard: MenuSetDashboard | null;
};

/**
 * 대시보드 한 벌 — 열쇠의 정본은 `constants/dashboard-widgets` 다.
 * ⚠️ `order` 에 없는 칸은 **뒤에 붙는다** — 새 칸이 생겨도 이미 저장된 줄에서 사라지지 않는다.
 * 🚨 **`custom` 이 만든 칸의 정의를 쥔다** — `order`·`hidden` 은 그 열쇠만 담는다. 정의가
 *   없는 `custom:` 열쇠는 읽을 때 떨어진다(`resolveDashboardOrder`).
 */
export type MenuSetDashboard = {
  order: DashboardWidgetId[];
  hidden: DashboardWidgetId[];
  custom: DashboardCustomWidget[];
};

/**
 * 아직 안 깎인 대시보드 — **받는 자리의 타입**이다.
 * 🔑 세 칸이 다 optional 이다 — 담는 길(`cleanDashboard`)이 어차피 깎으므로, 부르는 쪽이
 *   안 쓰는 칸까지 빈 배열로 채우게 만들지 않는다.
 */
export type MenuSetDashboardInput = Partial<MenuSetDashboard>;

export const EMPTY_MENU_FILTER: MenuSetFilter = {
  platforms: [], xPlatforms: [], instagramPlatforms: [], tiktokAdTypes: [], tiktokAdBoosts: [],
  country: [], languages: [], adsEval: [], isOfficialAccount: [], metricMin: null,
  excludeKeywords: [], mandatoryKeywords: [], refineText: '', refineFieldKeys: [],
  sortKey: '', beautyEval: false, canonicalBrandOnly: false, linkSearch: false,
};

/**
 * 기본 셋 셋 — 지금 고정 메뉴로 서 있는 그대로.
 * ⚠️ 여는 경로와 플랜 칸은 **여기가 아니라 메뉴 쪽이 안다**(`nav-config-service`) — 이 파일에
 *   경로를 적으면 두 벌이 된다.
 */
/**
 * 기본으로 주는 줄 — **수집 세트는 여기 없다**(세트마다 제 줄이 선다 · `syncCampaignSets`).
 * ⚠️ `keywordMonitoring` 은 타입에 있다 — 저장본에 그 줄이 있으면 읽어서 세트 줄들로 편다.
 */
export const BUILTIN_MENU_KEYS: BuiltinMenuKey[] = ['brandMention', 'brandAccount'];

/**
 * 저장본에서 **읽어 주는** 기본 열쇠 — 기본으로 «주는» 것보다 넓다.
 * ⚠️ `keywordMonitoring` 은 기본 셋이 아니지만 저장본에는 그 줄이 있다 — 읽어야
 *   `syncCampaignSets` 가 그 자리에서 세트 줄들로 편다. 안 읽으면 차례가 맨 뒤로 밀린다.
 */
const READABLE_BUILTIN_KEYS: BuiltinMenuKey[] = ['brandMention', 'keywordMonitoring', 'brandAccount'];

const BUILTIN_NAME: Record<BuiltinMenuKey, string> = {
  brandMention: '브랜드 멘션 추적',
  keywordMonitoring: '타겟 멘션 추적',
  brandAccount: '자사 계정 관리',
};

/** 기본 셋의 열쇠 — 이름을 바꿔도 이 값은 안 변한다 */
export function builtinId(key: BuiltinMenuKey): string {
  return `builtin:${key}`;
}

/** 아무것도 안 고친 워크스페이스가 보는 목록 */
export function defaultMenuSets(): MenuSet[] {
  return BUILTIN_MENU_KEYS.map((key) => ({
    id: builtinId(key),
    name: BUILTIN_NAME[key],
    hidden: false,
    source: { kind: 'builtin' as const, key },
    keywords: [],
    accounts: [],
    filter: EMPTY_MENU_FILTER,
    dashboard: null,
  }));
}

/**
 * 문서 필드 → 줄 목록.
 *
 * 🚨 **저장본을 그대로 따른다** — 빠진 기본 셋을 도로 붙이지 않는다(머리말). 붙이면 사용자가
 *   지운 줄이 되살아난다.
 * ⚠️ **필드가 아예 없을 때만** 기본 셋이다 — 빈 배열은 «비우기로 한 것»이라 빈 채로 둔다.
 */
export function parseMenuSets(raw: unknown): MenuSet[] {
  if (!Array.isArray(raw)) return defaultMenuSets();
  return raw.map(toMenuSet).filter((row): row is MenuSet => row !== null).slice(0, MENU_SET_CEILING);
}

/** 지금 빠져 있는 기본 셋 — 되돌리기 단추를 세울지 이것으로 판단한다 */
export function missingBuiltinSets(sets: MenuSet[]): MenuSet[] {
  const seen = new Set(sets.map((set) => set.id));
  return defaultMenuSets().filter((row) => !seen.has(row.id));
}

/**
 * 빠진 기본 셋을 **뒤에 도로 세운다**.
 * 🔑 있는 줄은 하나도 안 건드린다 — 차례도 이름도 그대로다.
 */
export function restoreBuiltinSets(sets: MenuSet[]): MenuSet[] {
  const missing = missingBuiltinSets(sets);
  if (missing.length === 0) return sets;
  return [...sets, ...missing].slice(0, MENU_SET_CEILING);
}

/** 기본 셋인가 — 「기본」 배지를 다는 판단이다(지우는 것은 막지 않는다) */
export function isBuiltinSet(set: MenuSet): boolean {
  return set.source.kind === 'builtin';
}

/** 이름을 바꾼다 — 빈 이름은 **그대로 둔다**(이름 없는 줄은 메뉴에서 사라진 것처럼 보인다) */
export function renameMenuSet(sets: MenuSet[], id: string, name: string): MenuSet[] {
  const value = String(name ?? '').trim().slice(0, 40);
  if (!value) return sets;
  return sets.map((set) => (set.id === id ? { ...set, name: value } : set));
}

/** 켜고 끈다 */
export function toggleMenuSet(sets: MenuSet[], id: string): MenuSet[] {
  return sets.map((set) => (set.id === id ? { ...set, hidden: !set.hidden } : set));
}

/** 한 칸 올리거나 내린다 — 끝에서는 그대로 돌려준다 */
export function moveMenuSet(sets: MenuSet[], id: string, delta: number): MenuSet[] {
  const from = sets.findIndex((set) => set.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= sets.length) return sets;
  const next = [...sets];
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
}

/**
 * 줄을 더한다 — 사용자가 만든 것만.
 * ⚠️ 상한을 넘기면 **그대로 돌려준다**(부르는 쪽이 같은 배열인지 보고 알린다).
 */
export function addMenuSet(
  sets: MenuSet[],
  input: Omit<Partial<MenuSet>, 'filter' | 'dashboard'> & {
    name: string;
    id: string;
    filter?: Partial<MenuSetFilter>;
    dashboard?: MenuSetDashboardInput | null;
  },
): MenuSet[] {
  const name = String(input.name ?? '').trim().slice(0, 40);
  if (!name || !input.id || sets.length >= MENU_SET_MAX) return sets;
  if (sets.some((set) => set.id === input.id)) return sets;
  return [
    ...sets,
    {
      id: input.id,
      name,
      hidden: false,
      source: input.source ?? { kind: 'own' },
      keywords: cleanList(input.keywords),
      accounts: cleanAccounts(input.accounts),
      filter: cleanFilter(input.filter),
      dashboard: cleanDashboard(input.dashboard),
    },
  ];
}

/**
 * 줄을 지운다 — **기본 셋도 지워진다**.
 * 🔑 되돌릴 길이 있어서 연다(`restoreBuiltinSets`) — 없으면 한 번 지우면 끝이라 못 열 일이다.
 */
/**
 * 줄 하나를 **내 화면에서 뺀다**.
 *
 * 🚨 **수집 세트가 낳은 줄은 «지워지지 않는다»** — 목록에서 걷어 내도 다음 읽기에
 *   `syncCampaignSets` 가 살아 있는 캠페인을 보고 다시 세운다. 그래서 그 줄은 **감춘다**
 *   (저장본에 `hidden: true` 로 남아야 다시 안 선다).
 * 🔑 캠페인 «자체»를 지우는 자리는 추적 관리의 수집 세트 구역이다 — 거기서 지우면 이 줄도
 *   함께 사라진다(캠페인이 없으면 `syncCampaignSets` 가 줄을 안 세운다).
 */
export function removeMenuSet(sets: MenuSet[], id: string): MenuSet[] {
  const target = sets.find((set) => set.id === id);
  if (!target) return sets;
  if (target.source.kind === 'campaign') {
    if (target.hidden) return sets;
    return sets.map((set) => (set.id === id ? { ...set, hidden: true } : set));
  }
  return sets.filter((set) => set.id !== id);
}

/**
 * 이 줄의 **대시보드 차례를 갈아 끼운다**.
 * 🔑 조건은 안 건드린다 — 같은 조건 위에 무엇을 어떤 차례로 그릴지만 바뀐다.
 * 🔑 `null` 을 주면 **기본으로 되돌린다**(브랜드 멘션 그대로).
 */
export function setMenuSetDashboard(
  sets: MenuSet[],
  id: string,
  next: unknown,
): MenuSet[] {
  return sets.map((set) => (set.id === id ? { ...set, dashboard: cleanDashboard(next) } : set));
}

/**
 * 저장된 차례 → **실제로 그릴 차례**. 저장이 없거나 새 칸이 생긴 경우를 여기서 메운다.
 * 🚨 저장본에 없는 붙박이 칸은 **뒤에 붙인다** — 새 칸이 생겨도 이미 저장된 줄에서 사라지지 않는다.
 * 🚨 **정의가 없는 `custom:` 열쇠는 떨군다** — 지운 칸의 열쇠가 차례에 남아 있으면 편집 중에
 *   내용 없는 빈 띠가 선다.
 * 🔑 정의는 있는데 차례에 없는 만든 칸은 **맨 뒤에 붙인다** — 더한 칸이 안 보이는 일이 없다.
 */
export function resolveDashboardOrder(dashboard: MenuSetDashboardInput | null): DashboardWidgetId[] {
  const defined = new Set((dashboard?.custom ?? []).map((row) => row.id));
  const saved = (dashboard?.order ?? []).filter(
    (id) => !isCustomDashboardWidgetId(id) || defined.has(id),
  );
  const seen = new Set(saved);
  return [
    ...saved,
    ...DASHBOARD_WIDGET_IDS.filter((id) => !seen.has(id)),
    ...[...defined].filter((id) => !seen.has(id)),
  ];
}

/** 이 줄이 담은 만든 칸들 — 저장이 없으면 하나도 없다 */
export function resolveDashboardCustom(dashboard: MenuSetDashboardInput | null): DashboardCustomWidget[] {
  return dashboard?.custom ?? [];
}

/** 감춘 칸 — 저장이 없으면 하나도 안 감춘다 */
export function resolveDashboardHidden(dashboard: MenuSetDashboardInput | null): Set<DashboardWidgetId> {
  return new Set(dashboard?.hidden ?? []);
}

/** 미리 지정한 필터를 갈아 끼운다 */
export function setMenuSetFilter(sets: MenuSet[], id: string, filter: Partial<MenuSetFilter>): MenuSet[] {
  return sets.map((set) => (set.id === id ? { ...set, filter: cleanFilter({ ...set.filter, ...filter }) } : set));
}

/** 새 줄의 열쇠 — 저장본 안에서만 유일하면 된다 */
export function nextMenuSetId(sets: MenuSet[], at: number = Date.now()): string {
  let id = `set:${at.toString(36)}`;
  let bump = 0;
  while (sets.some((set) => set.id === id)) {
    bump += 1;
    id = `set:${at.toString(36)}${bump}`;
  }
  return id;
}

/** 걸어 둔 축이 몇이나 되나 — 화면이 「필터 3」 배지를 그린다 */
export function countMenuSetFilters(filter: MenuSetFilter): number {
  const lists = LIST_KEYS.filter((key) => (filter[key] ?? []).length > 0).length
    + FLAG_LIST_KEYS.filter((key) => (filter[key] ?? []).length > 0).length;
  const scalars = [
    filter.metricMin && filter.metricMin > 0 ? 1 : 0,
    filter.refineText ? 1 : 0,
    filter.beautyEval ? 1 : 0,
    filter.canonicalBrandOnly ? 1 : 0,
    filter.linkSearch ? 1 : 0,
  ].filter((n) => n > 0).length;
  return lists + scalars;
}

/**
 * 줄이 담은 필터 → **사이드바가 쥐는 값**. 빈 축은 덜어 낸다.
 *
 * 🔑 **칸 이름이 이미 같다**(`platforms`·`country`·`languages`…) — 그래서 옮겨 담기가 아니라
 *   덜어 내기다. 안 덜면 「안 거름」이 빈 목록으로 걸려 사이드바가 켜진 것처럼 보인다.
 * ⚠️ **스위치 셋은 여기서 안 나간다**(`beautyEval`·`canonicalBrandOnly`·`linkSearch`) —
 *   사이드바 필터가 아니라 화면이 따로 쥔 값이다. 부르는 쪽이 갈라 쓴다.
 */
export function toSidebarFilter(filter: MenuSetFilter): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  LIST_KEYS.forEach((key) => {
    if ((filter[key] ?? []).length > 0) out[key] = filter[key];
  });
  FLAG_LIST_KEYS.forEach((key) => {
    if ((filter[key] ?? []).length > 0) out[key] = filter[key];
  });
  if (filter.metricMin && filter.metricMin > 0) out.metricMin = filter.metricMin;
  if (filter.refineText) out.refineText = filter.refineText;
  if (filter.sortKey) out.sortKey = filter.sortKey;
  return out;
}

/**
 * 사이드바가 쥔 값 → **줄이 담는 필터**.
 * 🔑 키 이름이 이미 같다 — 화이트리스트(`LIST_KEYS`·`FLAG_LIST_KEYS`)가 모르는 키를 떨군다.
 */
export function toMenuSetFilter(filter: Record<string, unknown>): MenuSetFilter {
  return cleanFilter(filter);
}

/** 메뉴에 세울 줄 — 끈 것은 빠진다 */
export function visibleMenuSets(sets: MenuSet[]): MenuSet[] {
  return sets.filter((set) => !set.hidden);
}

/**
 * 주소의 `?view=<id>` 가 가리키는 줄.
 * 🔑 「내 화면」 스위치(`MY_SCREENS_VISIBLE`)가 꺼져 있으면 언제나 `null` — 화면은 줄을 안 펴고 기본으로 열린다.
 * @returns 목록이 아직 안 왔거나 그 줄이 없으면 `null`
 */
export function findOpenedMenuSet(
  sets: MenuSet[],
  viewId: string,
  ready: boolean,
  visible: boolean = MY_SCREENS_VISIBLE,
): MenuSet | null {
  if (!visible || !viewId || !ready) return null;
  return sets.find((row) => row.id === viewId) ?? null;
}

/**
 * 기본 셋이 아닌 줄이 여는 **주소의 질의 부분**.
 *
 * 🚨 **언제나 `view=<줄 id>` 다** — 캠페인을 이은 줄도 마찬가지다. 캠페인 id 는 줄 안에
 *   (`source.campaignId`) 있고, 여는 화면이 그것으로 조회어를 읽는다. 옛 `set=<캠페인 id>` 길은
 *   분석 화면의 수집 세트용이라 브랜드 멘션이 모른다 — 그 열쇠로 열면 조건이 **하나도 안 펴지고**
 *   그냥 기본 화면이 뜬다(실측에서 잡혔다).
 * ⚠️ 기본 셋에는 주소가 없다 — 제 화면으로 가므로 빈 글을 돌려준다(부르는 쪽이 가린다).
 */
export function menuSetQuery(set: MenuSet): string {
  if (set.source.kind === 'builtin') return '';
  return `view=${encodeURIComponent(set.id)}`;
}

// ---- 이하 비공개 ----------------------------------------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** 아는 열쇠만, 중복 없이 — 모르는 열쇠는 떨군다(없는 칸을 가리키면 빈 자리가 생긴다) */
function cleanWidgetIds(value: unknown): DashboardWidgetId[] {
  const rows = Array.isArray(value) ? value : [];
  const seen = new Set<DashboardWidgetId>();
  return rows.filter((entry): entry is DashboardWidgetId => {
    if (!isDashboardWidgetId(entry) || seen.has(entry)) return false;
    seen.add(entry);
    return true;
  });
}

/**
 * 대시보드 차례 한 벌.
 * 🚨 **둘 다 비면 `null` 이다** — 「기본 그대로」와 「전부 켜고 기본 차례」는 같은 상태이고,
 *   빈 객체를 남기면 문서에 뜻 없는 값이 쌓인다.
 */
function cleanDashboard(value: unknown): MenuSetDashboard | null {
  const row = (value != null && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const order = cleanWidgetIds(row.order);
  const hidden = cleanWidgetIds(row.hidden);
  const custom = cleanCustomWidgets(row.custom);
  if (order.length === 0 && hidden.length === 0 && custom.length === 0) return null;
  return { order, hidden, custom };
}

/**
 * 만든 칸의 정의 — **읽을 때 축·기간·모양을 정본에 맞춰 깎는다**.
 * 🚨 모르는 값은 떨군다 — 옛 저장본이나 손으로 고친 문서가 조회를 이상한 조건으로 끌고 가지
 *   못하게 한다. 기간·모양이 목록에 없으면 그 칸 자체를 버린다(기본값으로 슬쩍 바꾸지 않는다 —
 *   사용자가 정한 것과 다른 수가 같은 이름으로 서는 편이 더 나쁘다).
 */
function cleanCustomWidgets(value: unknown): DashboardCustomWidget[] {
  const rows = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  const out: DashboardCustomWidget[] = [];
  rows.forEach((entry) => {
    const row = (entry != null && typeof entry === 'object' ? entry : {}) as Record<string, unknown>;
    const id = text(row.id);
    if (!isCustomDashboardWidgetId(id) || seen.has(id)) return;
    const label = text(row.label);
    if (!label) return;
    // ⚠️ `kind` 가 없으면 셈 칸이다 — 갈래가 하나뿐이던 때 담긴 줄을 위한 기본값이다
    const kind = text(row.kind);
    const widget = (() => {
      if (kind === 'overview') return cleanOverviewWidget(id, label, row);
      if (kind === 'pivot') return cleanPivotWidget(id, label, row);
      return cleanCountWidget(id, label, row);
    })();
    if (!widget) return;
    seen.add(id);
    out.push(widget);
  });
  return out;
}

function cleanCountWidget(
  id: `custom:${string}`,
  label: string,
  row: Record<string, unknown>,
): DashboardCustomWidget | null {
  const axisKeys = new Set<string>(DASHBOARD_COUNT_AXES.map((axis) => axis.key));
  const designKeys = new Set<string>(DASHBOARD_COUNT_DESIGNS.map((design) => design.key));
  const days = Number(row.days);
  if (!(DASHBOARD_COUNT_DAYS as readonly number[]).includes(days)) return null;
  const design = text(row.design);
  if (!designKeys.has(design)) return null;
  const axes = (Array.isArray(row.axes) ? row.axes : [])
    .map((axis) => text(axis))
    .filter((axis) => axisKeys.has(axis));
  return {
    id,
    kind: 'count',
    label,
    days: days as Extract<DashboardCustomWidget, { kind: 'count' }>['days'],
    axes: [...new Set(axes)] as Extract<DashboardCustomWidget, { kind: 'count' }>['axes'],
    design: design as Extract<DashboardCustomWidget, { kind: 'count' }>['design'],
    span: toDashboardWidgetSpan(row.span),
  };
}

/**
 * 피벗 칸의 정의 — **축·지표·그림이 정본에 있어야** 살린다.
 * 🚨 모르는 축을 그대로 보내면 서버가 그 축을 조용히 떨궈, 화면에 「전체 한 줄」이 뜬다.
 *   그러느니 칸을 버리는 편이 낫다(무엇이 잘못됐는지 보인다).
 * ⚠️ 열 축은 **비어 있어도 된다** — 「안 나눈다」는 뜻이다.
 */
function cleanPivotWidget(
  id: `custom:${string}`,
  label: string,
  row: Record<string, unknown>,
): DashboardCustomWidget | null {
  const axes = new Set<string>(ANALYTICS_AXES.map((axis) => axis.key));
  const metrics = new Set<string>(ANALYTICS_METRICS.map((metric) => metric.key));
  const charts = new Set<string>(ANALYTICS_CHARTS.map((chart) => chart.key));
  const rowAxis = text(row.rowAxis);
  const columnAxis = text(row.columnAxis);
  const metric = text(row.metric);
  const chart = text(row.chart);
  const days = Number(row.days ?? 0);
  if (!axes.has(rowAxis) || !metrics.has(metric) || !charts.has(chart)) return null;
  if (columnAxis && !axes.has(columnAxis)) return null;
  if (!(DASHBOARD_PIVOT_DAYS as readonly number[]).includes(days)) return null;
  return { id, kind: 'pivot', label, rowAxis, columnAxis, metric, chart, days, span: toDashboardWidgetSpan(row.span) };
}

function cleanOverviewWidget(
  id: `custom:${string}`,
  label: string,
  row: Record<string, unknown>,
): DashboardCustomWidget | null {
  const cardKeys = new Set<string>(DASHBOARD_OVERVIEW_CARDS.map((card) => card.key));
  const designKeys = new Set<string>(DASHBOARD_OVERVIEW_DESIGNS.map((design) => design.key));
  const card = text(row.card);
  const design = text(row.design);
  if (!cardKeys.has(card) || !designKeys.has(design)) return null;
  return {
    id,
    kind: 'overview',
    label,
    card: card as Extract<DashboardCustomWidget, { kind: 'overview' }>['card'],
    design: design as Extract<DashboardCustomWidget, { kind: 'overview' }>['design'],
    span: toDashboardWidgetSpan(row.span),
  };
}

function cleanList(value: unknown): string[] {
  return Array.isArray(value) ? [...new Set(value.map(text).filter(Boolean))] : [];
}

function cleanAccounts(value: unknown): MenuSetAccount[] {
  const rows = Array.isArray(value) ? value : [];
  const seen = new Set<string>();
  return rows
    .map((entry) => {
      const row = (entry != null && typeof entry === 'object' ? entry : {}) as Record<string, unknown>;
      return { platform: text(row.platform).toLowerCase(), account: text(row.account) };
    })
    .filter((row) => {
      if (!row.platform || !row.account) return false;
      const key = `${row.platform}*${row.account.toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function cleanFilter(value: unknown): MenuSetFilter {
  const row = (value != null && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const metricMin = Number(row.metricMin);
  const lists = Object.fromEntries(LIST_KEYS.map((key) => [key, cleanList(row[key])]));
  const flags = Object.fromEntries(FLAG_LIST_KEYS.map((key) => [key, cleanFlagList(row[key])]));
  return {
    ...(lists as Record<(typeof LIST_KEYS)[number], string[]>),
    ...(flags as Record<(typeof FLAG_LIST_KEYS)[number], boolean[]>),
    metricMin: Number.isFinite(metricMin) && metricMin > 0 ? Math.trunc(metricMin) : null,
    refineText: typeof row.refineText === 'string' ? row.refineText.trim().slice(0, 100) : '',
    sortKey: typeof row.sortKey === 'string' ? row.sortKey.trim().slice(0, 40) : '',
    beautyEval: row.beautyEval === true,
    canonicalBrandOnly: row.canonicalBrandOnly === true,
    linkSearch: row.linkSearch === true,
  } as MenuSetFilter;
}

/** 참·거짓 목록 — 둘뿐이라 중복만 접는다 */
function cleanFlagList(value: unknown): boolean[] {
  const rows = Array.isArray(value) ? value : [];
  return [...new Set(rows.filter((entry): entry is boolean => typeof entry === 'boolean'))];
}

/** 저장본 한 줄 → 줄. 열쇠나 이름이 없으면 버린다(메뉴에 못 세운다) */
function toMenuSet(value: unknown): MenuSet | null {
  const row = (value != null && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const id = text(row.id);
  const name = text(row.name).slice(0, 40);
  if (!id || !name) return null;
  return {
    id,
    name,
    hidden: row.hidden === true,
    source: toSource(row.source),
    keywords: cleanList(row.keywords),
    accounts: cleanAccounts(row.accounts),
    filter: cleanFilter(row.filter),
    dashboard: cleanDashboard(row.dashboard),
  };
}

function toSource(value: unknown): MenuSetSource {
  const row = (value != null && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const kind = text(row.kind);
  if (kind === 'builtin') {
    const key = text(row.key) as BuiltinMenuKey;
    // ⚠️ 모르는 기본 열쇠는 `own` 으로 떨군다 — 없는 화면을 여는 줄이 서면 빈 화면이 열린다
    return READABLE_BUILTIN_KEYS.includes(key) ? { kind: 'builtin', key } : { kind: 'own' };
  }
  if (kind === 'campaign') {
    const campaignId = text(row.campaignId);
    return campaignId ? { kind: 'campaign', campaignId } : { kind: 'own' };
  }
  return { kind: 'own' };
}

/** 캠페인 줄의 열쇠 — 캠페인 하나에 줄 하나다(이름을 바꿔도 안 변한다) */
export function campaignSetId(campaignId: string): string {
  return `campaign:${String(campaignId ?? '').trim()}`;
}

/**
 * **수집 세트마다 제 줄을 세운다** — 저장본과 지금 캠페인 목록을 맞춘다.
 *
 * ```
 *   저장본에 없는 캠페인   → 줄을 뒤에 세운다(이름은 캠페인 이름)
 *   사라진 캠페인의 줄     → 걷는다(눌러도 빈 화면이 열린다)
 *   `builtin:keywordMonitoring` 줄 → **그 자리에서** 세트 줄들로 편다(차례·감춤을 물려준다)
 * ```
 *
 * 🚨 **세트 하나가 줄 하나다** — 「타겟 멘션 추적」 한 줄로 묶어 두면 이름도 차례도
 *   켜고 끄기도 캠페인 묶음 통째로만 되어, 캠페인 하나를 따로 둘 수가 없다.
 * 🔑 **있는 줄은 안 건드린다** — 이름·차례·감춤·필터가 그대로다. 새 캠페인만 뒤에 붙는다.
 * ⚠️ 저장하지 않는다 — 읽을 때마다 맞춘다. 사용자가 무엇을 고칠 때 그 결과가 저장된다.
 */
export function syncCampaignSets(
  sets: MenuSet[],
  campaigns: readonly Record<string, any>[],
): MenuSet[] {
  const live = new Map<string, string>();
  campaigns.forEach((row) => {
    const id = String(row?.id ?? '').trim();
    if (id) live.set(id, String(row?.name ?? '').trim() || '이름 없는 세트');
  });

  const make = (campaignId: string, hidden: boolean): MenuSet => ({
    id: campaignSetId(campaignId),
    name: live.get(campaignId) ?? '이름 없는 세트',
    hidden,
    source: { kind: 'campaign', campaignId },
    keywords: [],
    accounts: [],
    filter: EMPTY_MENU_FILTER,
    dashboard: null,
  });

  const seen = new Set<string>();
  const out: MenuSet[] = [];
  sets.forEach((set) => {
    // 묶음 줄은 그 자리에서 세트 줄들로 편다(머리말)
    if (set.source.kind === 'builtin' && set.source.key === 'keywordMonitoring') {
      live.forEach((_name, id) => {
        if (seen.has(id)) return;
        seen.add(id);
        out.push(make(id, set.hidden));
      });
      return;
    }
    if (set.source.kind === 'campaign') {
      const id = set.source.campaignId;
      // 사라진 캠페인의 줄은 걷는다 — 눌러도 빈 화면이 열린다
      if (!live.has(id) || seen.has(id)) return;
      seen.add(id);
      out.push(set);
      return;
    }
    out.push(set);
  });

  live.forEach((_name, id) => {
    if (seen.has(id)) return;
    out.push(make(id, false));
  });

  return out.slice(0, MENU_SET_CEILING);
}

/**
 * 두 줄의 **자리를 맞바꾼다** — 차례를 구역 안에서 옮길 때 쓴다.
 *
 * 🔑 **구역이 갈린 목록에서 «한 칸 위»는 이웃이 아니다** — 다른 구역의 줄을 건너뛰어야
 *   눌렀을 때 그 구역 안에서 한 칸 움직인다(`moveMenuSet` 은 배열의 바로 옆과 바꾼다).
 * ⚠️ 못 찾거나 같은 줄이면 **같은 배열**을 돌려준다 — 부르는 쪽이 「바뀐 것이 없다」를 안다.
 */
export function swapMenuSets(sets: MenuSet[], idA: string, idB: string): MenuSet[] {
  const a = sets.findIndex((set) => set.id === idA);
  const b = sets.findIndex((set) => set.id === idB);
  if (a < 0 || b < 0 || a === b) return sets;
  const next = [...sets];
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}
